import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import { t } from "i18next";

import {
  ACTION_KEYS,
  INITIAL_REPLY_OPTIONS,
  SHOW_ACTION_KEYS,
  areQuickActionsEmpty,
  compMsgKeys,
  getNextOptions,
  hotKeysOptions,
  makeNextOptions,
  optionTypes,
  wrapLines,
} from "utils";
import { Icon, CustomTooltip } from "components";
import {
  addNewMessage,
  getHeaderBgColor,
  getVisibleMascot,
  removeAndAddMessages,
  selectMascotSettings,
  selectPromotionOffer,
  setIsDisabled,
  setIsOpen,
  setOptionsTypeLogs,
  setReplies,
  setShowLangChange,
  setVisibleMascot,
  getConversationStopped,
  setModuleName,
  getAMAReply,
  selectReplies,
  getAppSSID,
} from "slices";
import { createEventSignal, hasDateInText, initConversationWithMessages } from "utils/methods/index";

const HotKeys = (props) => {
  const { position, openChatApp, createHayleyRecord, hotkeysRef } = props;

  const dispatch = useDispatch();
  const replies = useSelector(selectReplies);
  const visible = useSelector(getVisibleMascot);
  const mascotSettings = useSelector(selectMascotSettings);
  const promotionOffer = useSelector(selectPromotionOffer);
  const conversationStopped = useSelector(getConversationStopped);
  const amaReply = useSelector(getAMAReply);
  const backgroundColor = useSelector(getHeaderBgColor);
  const SSID = useSelector(getAppSSID);

  const [isHovered, setIsHovered] = useState(false);

  const hideMascot = () => {
    dispatch(setVisibleMascot(false));

    // Tell iframe to hide
    window.parent.postMessage(
      {
        type: "hayley-hide-chatbot",
      },
      "*"
    );
  };

  const onClickHotKey = (key) => {
    dispatch(setIsOpen(true));

    const urlParams = new URLSearchParams(window.location.search);
    const propertyId = urlParams.get("property_id");

    const requestOption = {
      tag: "SHOW_TO_CONVERSATION",
      metric: "Conversation",
      propertyId: propertyId,
      sessionId: SSID,
      appSubsection: "Start a conversation",
    };
    localStorage.removeItem(`events_${SSID}`);
    createEventSignal(requestOption);

    dispatch(setShowLangChange(false));
    dispatch(setModuleName(key));

    const eventHash = {
      event_name: "h_click",
      event_params: {
        h_page_type: "hayley",
        h_clicked_location: key,
        h_stream: "hyly",
        // h_tour_type: label,
        h_display_method: "iframe",
      },
    };

    window.parent.postMessage(JSON.stringify(eventHash), "*");

    dispatch(setIsDisabled(true));

    // set replies store value for the find_home or schedule_tour
    const replyOptionData = INITIAL_REPLY_OPTIONS.find((item) => item.key === key);

    const newMsgs = replyOptionData.message?.map((v) => ({
      ...v,
      optionsType: "Init",
    }));

    dispatch(addNewMessage(newMsgs));

    if (replyOptionData.nextOptionsType) {
      const message = optionTypes[replyOptionData.nextOptionsType] || `typing`;
      dispatch(addNewMessage([{ message: message, speaker: "bot", createdAt: new Date().toISOString() }]));

      getNextOptions(replyOptionData.nextOptionsType).then((res) => {
        const { nextReplies, nextBotMessages } = makeNextOptions(replyOptionData.nextOptionsType, res.data, res.extraData || undefined);

        if (nextBotMessages?.length && newMsgs?.length) {
          initConversationWithMessages(SSID, newMsgs, nextBotMessages);
        }

        dispatch(removeAndAddMessages(nextBotMessages));

        dispatch(setReplies(nextReplies));
        dispatch(setIsDisabled(false));

        const targetKeys = ["noCommunityInfoWhatNext", "noVideoWhatNext", "noPhotoWhatNext"];

        const hasMatchingKey = nextBotMessages.some((obj) => targetKeys.includes(obj.message));

        if (hasMatchingKey) {
          return;
        }

        const { nextOptionsType } = replyOptionData;

        if (["Photos", "Videos", "CommunityInfo"].includes(nextOptionsType)) {
          if (nextReplies.options.length) {
            const { nextOptionsType: type, data: getData } = nextReplies.options[0];

            getNextOptions(type, getData, null, null, null, null, null, amaReply?.photos).then((res) => {
              const { nextReplies } = makeNextOptions(type, res.data, res.extraData || undefined);

              setTimeout(() => {
                dispatch(
                  addNewMessage([
                    {
                      key: compMsgKeys[nextReplies?.renderType] || replies?.renderType,
                      speaker: "comp",
                      createdAt: new Date(),
                      message: amaReply?.message,
                      images: res?.data,
                    },
                  ])
                );

                dispatch(setIsDisabled(false));
              }, 400);

              dispatch(setReplies(nextReplies));
            });
          }
        }

        dispatch(setOptionsTypeLogs(["Init"])); // Hotkey actions are always the first action
      });
    }

    dispatch(setIsOpen(true)); // After setting replies data, open the chatbot

    if (hotKeysOptions?.[key]) {
      createHayleyRecord(hotKeysOptions[key]);
    }
  };

  if (areQuickActionsEmpty(mascotSettings) && !mascotSettings?.welcomeMessage) {
    return null;
  }

  return (
    <div
      className={`hayley-flex hayley-translate-y-1 hayley-flex-col hayley-space-y-2 hayley-pt-[40px] hayley-font-roboto  hayley-transition-all hayley-duration-700 hayley-ease-in-out ${
        position === "bottom-right" ? "hayley-items-end" : "hayley-item-start"
      } ${visible ? "hayley-visible" : "hayley-hidden"}`}
      ref={hotkeysRef}
    >
      <CustomTooltip
        title={t("close")}
        placement="top"
        popperProps={{
          modifiers: [
            {
              name: "flip",
              enabled: false,
            },
          ],
        }}
      >
        <div
          className={`hayley-flex hayley-h-5 hayley-w-5 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-whitespace-nowrap hayley-rounded hayley-border hayley-border-custom-11 hayley-bg-white hayley-text-secondary hayley-shadow`}
          onClick={hideMascot}
        >
          <CloseIcon className="hayley-h-full hayley-w-full" sx={{ "&:hover": { color: `${backgroundColor} !important` } }} />
        </div>
      </CustomTooltip>
      {conversationStopped ? (
        <Box
          data-testid="continue-conversation-box"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`hayley-w-fit hayley-cursor-pointer hayley-whitespace-nowrap hayley-rounded hayley-border hayley-border-custom-11 hayley-bg-white hayley-px-4 hayley-py-[6px] hayley-text-secondary hayley-shadow hover:hayley-text-white`}
          sx={{ "&:hover": { backgroundColor: `${backgroundColor} !important` } }}
          onClick={openChatApp}
        >
          <Icon
            iconType="Arrow"
            className={`hayley-mr-2 hayley-h-3 hayley-w-3 hayley-rotate-180 hayley-text-secondary ${isHovered ? "hayley-text-white" : ""}`}
          />
          {t("continueConversation")}
        </Box>
      ) : mascotSettings?.welcomeMessage && mascotSettings.bubbleType === "welcome_message" ? (
        <div className="hayley-w-fit hayley-cursor-default hayley-whitespace-nowrap hayley-rounded hayley-border hayley-border-custom-11 hayley-bg-white hayley-px-4 hayley-py-[6px] hayley-text-secondary hayley-shadow">
          {wrapLines(mascotSettings?.welcomeMessage || "", 35).map((line, index) =>
            index == 0 ? (
              <p key={index} className={`hayley-my-0 ${position === "bottom-right" ? "hayley-text-right" : "hayley-text-left"}`}>
                <Icon iconType="WelcomeMessage" className="mr-2.5" /> {line}
              </p>
            ) : (
              <p key={index} className={`hayley-my-0 ${position === "bottom-right" ? "hayley-text-right" : "hayley-text-left"}`}>
                {line}
              </p>
            )
          ) || ""}
        </div>
      ) : mascotSettings.bubbleType === "quick_actions" ? (
        <>
          {mascotSettings?.quickActions
            ?.filter((action) => action)
            ?.map((action, idx) => (
              <Box
                data-testid="hot-keys"
                key={idx}
                className={`hayley-w-fit hayley-cursor-pointer hayley-whitespace-nowrap hayley-rounded hayley-border hayley-border-custom-11 hayley-bg-white hayley-px-4 hayley-py-[6px] hayley-text-secondary hayley-shadow hover:hayley-text-white`}
                sx={{ "&:hover": { backgroundColor: `${backgroundColor} !important` } }}
                onClick={() => onClickHotKey(ACTION_KEYS?.[action] || action)}
              >
                {/* {convertToTitleCase(action)} */}
                {t(SHOW_ACTION_KEYS[action])}
              </Box>
            ))}
        </>
      ) : mascotSettings.bubbleType === "offers" ? (
        <div className="hayley-w-fit hayley-cursor-text hayley-whitespace-nowrap hayley-rounded hayley-border hayley-border-custom-11 hayley-bg-white hayley-px-4 hayley-py-[6px] hayley-text-secondary hayley-shadow">
          <Box
            data-testid="offer-container"
            className={`hayley-m-0 hayley-cursor-pointer hayley-p-0 ${position === "bottom-right" ? "hayley-text-right" : "hayley-text-left"}`}
            onClick={() => (promotionOffer?.cta_type ? onClickHotKey(ACTION_KEYS?.[promotionOffer?.cta_type] || promotionOffer?.cta_type) : null)}
          >
            {wrapLines(promotionOffer?.description || "", 35).map((line, index) =>
              index == 0 ? (
                <p key={index} className={`hayley-my-0`}>
                  {line}
                </p>
              ) : (
                <p key={index} className={`hayley-my-0`}>
                  {line}
                </p>
              )
            ) || ""}
            {(() => {
              const hasDate = hasDateInText(promotionOffer?.dynamic_end_date_text);

              return hasDate ? (
                <p className="hayley-m-0 hayley-p-0 hayley-py-2 hayley-text-sm hayley-text-[#666666]">{promotionOffer?.dynamic_end_date_text}</p>
              ) : null;
            })()}

            <p className={`hayley-m-0 hayley-p-0 hayley-text-[16px] hayley-font-semibold`}>{t(SHOW_ACTION_KEYS[promotionOffer?.cta_type])}</p>
          </Box>
        </div>
      ) : null}
    </div>
  );
};

export default HotKeys;
